/**
 * Tela dos pedidos de compra.
 *
 * Papel: junta o quadro dos pedidos em andamento, a seção dos finalizados e as
 * caixas que registram uma decisão — aprovação, motivo da recusa, compra — e a
 * que pergunta o destino das despesas ao voltar um pedido já lançado. O
 * formulário mora em endereço próprio — `/compras/novo` e `/compras/:id` —,
 * aberto pela `PaginaFormularioPedido`.
 *
 * Como usar: quem tem nível `ver` não recebe o "+ Novo pedido de compra" nem os
 * botões de movimento, e o cartão e a linha deixam de responder ao clique. O
 * quadro e a tabela continuam legíveis, e são tudo o que esse nível enxerga de
 * um pedido, porque `PaginaFormularioPedido` exige `editar`: o cartão traz
 * número, casa, itens, fornecedor, valor, data, solicitante e categoria, e a
 * linha de finalizados traz descrição, número, data, casa, fornecedor,
 * categoria, solicitante, aprovador, comprador, resultado e valor. Um campo
 * tirado do cartão ou da tabela deixa de existir, em qualquer tela, para quem
 * só tem `ver`.
 *
 * As duas etapas que exigem decisão não avançam pelo botão: `proximoStatus`
 * devolve a mesma etapa com `decisao` preenchida, e a tela abre a caixa que pede
 * quem aprovou — ou quem comprou — antes de gravar. É o que impede um pedido
 * atravessar a aprovação sem aprovador registrado. A reprovação passa sempre
 * pela caixa do motivo, venha ela do "✕ Reprovar" do cartão ou do da caixa de
 * aprovação. A caixa de compra recusa, sem gravar nenhum, o lote em que algum
 * pedido tenha item sem categoria financeira, e lista os números pendentes.
 *
 * Retroceder apaga o que a etapa preencheu, seguindo `statusAnterior`: voltar do
 * processo de compra limpa a aprovação, voltar de comprado limpa o comprador, e
 * um finalizado por reprovação volta para a aprovação com o motivo apagado. O
 * "reabrir" da tabela de finalizados é o mesmo retrocesso.
 *
 * **O pedido que já gerou despesa não retrocede direto.** `lancadoNoFinanceiro`
 * abre a caixa que pergunta o destino dos lançamentos, e a resposta viaja em
 * `lancamentos`. O pedido volta nos dois casos — o que a escolha muda é só se as
 * despesas vão junto —, e nenhum dos dois é seguro por padrão: apagar sozinho
 * tiraria do caixa um lançamento que alguém pode já ter conciliado, e manter
 * sozinho deixaria a despesa de uma compra que deixou de existir.
 *
 * **Sair de "Em Cotação" imprime a folha de aprovação no mesmo clique.** O
 * avanço da cotação para a aprovação busca as fotos ao lado da gravação, sem
 * esperar por ela, e monta a `FolhaDeAprovacao` assim que as imagens chegam —
 * a janela de impressão abre colada ao clique, e não depois da gravação e da
 * nova leitura da listagem. A gravação que falha aparece no bloco de erro
 * ainda que a folha já tenha saído, e o pedido continua na cotação. A busca de
 * foto que falha deixa o pedido avançado e mostra a falha no lugar da folha.
 * Enquanto a folha está montada, o resto da tela leva `print:hidden`.
 *
 * **A seleção múltipla age sobre os cartões marcados, um pedido por gravação.**
 * Com algum cartão marcado, a barra grudada no topo oferece "🖨 Exportar PDF",
 * "← Retroceder selecionados", "Avançar selecionados →" e "Limpar seleção".
 * Avançar e retroceder em lote aplicam a cada pedido a regra do botão do
 * cartão, gravando em sequência, na ordem de marcação: os que saem de "Em
 * Cotação" entram juntos numa folha só, impressa no clique; os que pedem quem
 * aprovou, ou quem comprou, entram juntos numa caixa só, cuja resposta vale
 * para todos — a de aprovação primeiro, e a de compra no clique seguinte —; e
 * os que já geraram despesa entram juntos numa pergunta só sobre os
 * lançamentos. Quem grava sai da seleção; quem falha fica marcado e aparece
 * pelo número no bloco de erro da página, sem desfazer os que gravaram.
 * Confirmar uma caixa de decisão nesta tela — de um cartão ou do lote —
 * esvazia a seleção inteira.
 *
 * Com uma folha a imprimir e uma caixa de decisão a abrir no mesmo lote, a
 * caixa espera a janela de impressão fechar — a promessa de
 * `folhaDeAprovacao.gerar` só cumpre aí: aberta antes, ela sairia no papel,
 * porque o `AlertDialog` mora fora do bloco que leva `print:hidden`.
 *
 * **A compra mostra o que foi lançado.** Confirmada a caixa de compra, a
 * "Lançado no Financeiro" lista as despesas que a API acabou de gravar no caixa
 * para os pedidos que gravaram — as de `despesasAvisadas`, a mesma regra da
 * API. O pedido que volta a `comprado` mantendo a despesa de antes não aparece
 * nela. O formulário que grava a compra volta para cá com as despesas no
 * estado da navegação, e a mesma caixa abre por cima do quadro; fechá-la limpa
 * esse estado, para que recarregar a página não a abra de novo.
 *
 * `gravarTransicao` remonta o pedido campo a campo porque a gravação substitui o
 * registro inteiro. Um campo novo em `novoPedidoCompraSchema` que não seja
 * acrescentado ali passa a ser apagado a cada avanço ou retrocesso.
 */
import {
  type PedidoCompra,
  ROTULOS_MODULO,
  STATUS_NO_QUADRO,
  acessoAlcanca,
  posicoesSemCategoria,
  proximoStatus,
  statusAnterior,
} from '@concierge/shared';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { ErroDaApi } from '@/shared/api';
import { useAcesso } from '@/shared/hooks';
import { cn } from '@/shared/lib/utils';
import { hojeEmIso } from '@/shared/lib/vencimento';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import { CabecalhoDoModulo } from '@/shared/ui/cabecalho-do-modulo';
import { Skeleton } from '@/shared/ui/skeleton';
import { Vazio } from '@/shared/ui/vazio';
import { AvisoLancadoNoFinanceiro } from '../components/aviso-lancado-no-financeiro';
import {
  CaixaDeAprovacao,
  CaixaDeCompra,
  CaixaDeReprovacao,
} from '../components/caixas-de-decisao';
import { FolhaDeAprovacao } from '../components/folha-de-aprovacao';
import { QuadroCompras } from '../components/quadro-compras';
import { TabelaFinalizados } from '../components/tabela-finalizados';
import { avisoDaCompraSemCategoriaNoQuadro } from '../classificacao';
import { type PedidoNaFolha, pedidoGravadoParaFolha } from '../folha-de-aprovacao';
import {
  type DespesaAvisada,
  despesasAvisadas,
  despesasDoEstado,
  lancaNaVirada,
} from '../lancado-no-financeiro';
import { usePedidosCompra, useSalvarPedido } from '../hooks/usar-compras';
import { prepararFolha, useFolhaDeAprovacao } from '../hooks/usar-folha-de-aprovacao';
import { useSelecaoDePedidos } from '../hooks/usar-selecao-de-pedidos';

/**
 * Qual caixa de decisão está aberta, e para quais pedidos — um, vindo do
 * cartão, ou vários, vindos de "Avançar selecionados →".
 */
type DecisaoAberta =
  | { modo: 'fechada' }
  | { modo: 'aprovacao' | 'reprovacao' | 'compra'; pedidos: readonly PedidoCompra[] };

/** Os pedidos à espera da pergunta sobre os lançamentos, e se vieram do lote. */
interface RetrocessoComDespesa {
  pedidos: readonly PedidoCompra[];
  lote: boolean;
}

/** O que sobrou de uma gravação em sequência. */
interface ResultadoDoLote {
  /** Os pedidos que gravaram, como ficaram gravados, na ordem de entrada. */
  gravados: PedidoCompra[];
  /** Os pedidos que a gravação recusou, com o que ela rejeitou. */
  falhas: { pedido: PedidoCompra; falha: unknown }[];
}

/**
 * Traduz a falha de uma gravação no texto que a tela mostra.
 *
 * @param falha - O que a gravação rejeitou.
 * @param padrao - O texto para uma falha que nem chegou ao servidor.
 * @returns A mensagem da API, ou `padrao`.
 */
function mensagemDaFalha(falha: unknown, padrao: string): string {
  return falha instanceof ErroDaApi ? falha.message : padrao;
}

/**
 * O número do pedido como a mensagem de falha o escreve.
 *
 * @param pedido - O pedido que falhou.
 * @returns O `numero`, ou "sem número" quando ele está vazio.
 */
function numeroNaMensagem(pedido: PedidoCompra): string {
  return pedido.numero === '' ? 'sem número' : pedido.numero;
}

/**
 * A mensagem das gravações que falharam num lote, com o número de cada pedido.
 *
 * @param acao - O que se tentou, no infinitivo, como "avançar" ou "registrar a
 *   compra".
 * @param falhas - Os pedidos recusados e o que a gravação rejeitou.
 * @returns "Não foi possível avançar: PC-1, PC-2.", seguida das mensagens
 *   distintas que a API devolveu.
 */
function mensagemDoLote(acao: string, falhas: ResultadoDoLote['falhas']): string {
  const numeros = falhas.map(({ pedido }) => numeroNaMensagem(pedido)).join(', ');
  const motivos = new Set(
    falhas.flatMap(({ falha }) => (falha instanceof ErroDaApi ? [falha.message] : [])),
  );
  return [`Não foi possível ${acao}: ${numeros}.`, ...motivos].join(' ');
}

/**
 * O aviso de que os pedidos saíram da cotação sem a folha de aprovação.
 *
 * @param quantos - Quantos pedidos a folha levaria.
 * @returns A frase no singular ou no plural, com o caminho para imprimir depois.
 */
function avisoDaFolhaQueNaoSaiu(quantos: number): string {
  return quantos === 1
    ? 'O pedido avançou, mas a folha de aprovação não saiu: não foi possível carregar as fotos dos itens. Abra o pedido e use "🖨 Imprimir / PDF".'
    : 'Os pedidos avançaram, mas a folha de aprovação não saiu: não foi possível carregar as fotos dos itens. Abra cada pedido e use "🖨 Imprimir / PDF".';
}

/**
 * Se o pedido está numa das colunas do quadro.
 *
 * @param pedido - O pedido marcado.
 * @returns Falso para o finalizado, que não tem cartão nem conta na seleção.
 */
function estaNoQuadro(pedido: PedidoCompra): boolean {
  return STATUS_NO_QUADRO.some((etapa) => etapa === pedido.status);
}

/**
 * Página dos pedidos de compra.
 *
 * @returns A tela com o quadro, os finalizados e as caixas de decisão.
 */
export function PaginaCompras() {
  const navigate = useNavigate();
  const location = useLocation();
  const [decisao, setDecisao] = useState<DecisaoAberta>({ modo: 'fechada' });
  // O formulário que grava a compra volta para cá com as despesas no estado da
  // navegação, e a caixa abre por cima do quadro, como abre depois da caixa de
  // compra daqui.
  const [despesasLancadas, setDespesasLancadas] = useState<readonly DespesaAvisada[]>(() =>
    despesasDoEstado(location.state),
  );
  const [aDesfazer, setADesfazer] = useState<RetrocessoComDespesa | null>(null);
  const [erroDaAcao, setErroDaAcao] = useState<string | null>(null);
  const [erroDaCaixa, setErroDaCaixa] = useState<string | null>(null);
  const [emLote, setEmLote] = useState(false);

  const acesso = useAcesso();
  const podeEditar =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'compras', 'editar');

  const consultaPedidos = usePedidosCompra();
  const salvar = useSalvarPedido();
  const folhaDeAprovacao = useFolhaDeAprovacao();
  const selecao = useSelecaoDePedidos();

  const pedidos = consultaPedidos.data ?? [];

  const porId = new Map(pedidos.map((pedido) => [pedido.id, pedido]));
  const selecionados = [...selecao.ids].flatMap((id) => {
    const marcado = porId.get(id);
    return marcado !== undefined && estaNoQuadro(marcado) ? [marcado] : [];
  });

  /**
   * Grava o pedido inteiro com os campos que a transição mudou.
   *
   * @returns O pedido com as mudanças, como ficou gravado.
   */
  async function gravarTransicao(
    pedido: PedidoCompra,
    mudancas: Partial<PedidoCompra>,
    lancamentos?: 'excluir' | 'manter',
  ): Promise<PedidoCompra> {
    const completo = { ...pedido, ...mudancas };
    await salvar.mutateAsync({
      id: pedido.id,
      lancamentos,
      dados: {
        residenciaId: completo.residencia?.id ?? null,
        numero: completo.numero,
        data: completo.data,
        status: completo.status,
        solicitante: completo.solicitante,
        canalOrigem: completo.canalOrigem,
        categoria: completo.categoria,
        observacao: completo.observacao,
        aprovador: completo.aprovador,
        dataAprovacao: completo.dataAprovacao,
        compradoPor: completo.compradoPor,
        motivoReprovacao: completo.motivoReprovacao,
        lancadoNoFinanceiro: completo.lancadoNoFinanceiro,
        // `id` é o que faz a imagem sobreviver: a gravação substitui os itens por
        // inteiro, e a linha que chega sem identificador é tratada como nova — o
        // que gravaria `foto: null` e apagaria a imagem a cada avanço ou
        // retrocesso. A imagem em si continua fora, porque ausente significa
        // "não mexa"; o que não pode faltar é a identidade da linha.
        //
        // O mesmo vale para o resto do item, inclusive o que a transição não
        // toca: o campo que faltar aqui é apagado.
        itens: completo.itens.map((item) => ({
          id: item.id,
          descricao: item.descricao,
          quantidade: item.quantidade,
          valorUnitario: item.valorUnitario,
          desconto: item.desconto,
          acrescimo: item.acrescimo,
          fornecedor: item.fornecedor,
          categoriaFinanceira: item.categoriaFinanceira,
          subcategoriaFinanceira: item.subcategoriaFinanceira,
          formaPagamento: item.formaPagamento,
          statusFinanceiro: item.statusFinanceiro,
          dataCompra: item.dataCompra,
          parcelas: item.parcelas,
          intervaloParcelamento: item.intervaloParcelamento,
        })),
      },
    });
    return completo;
  }

  /**
   * Grava a transição de cada pedido, um depois do outro.
   *
   * Uma falha não interrompe o lote: os pedidos seguintes continuam sendo
   * gravados, e a falha volta em `falhas` para quem chamou mostrá-la.
   *
   * @returns Os que gravaram e os que falharam.
   */
  async function gravarEmSequencia(
    lote: readonly PedidoCompra[],
    mudancasDe: (pedido: PedidoCompra) => Partial<PedidoCompra>,
    lancamentos?: 'excluir' | 'manter',
  ): Promise<ResultadoDoLote> {
    const resultado: ResultadoDoLote = { gravados: [], falhas: [] };
    for (const pedido of lote) {
      try {
        resultado.gravados.push(
          await gravarTransicao(pedido, mudancasDe(pedido), lancamentos),
        );
      } catch (falha) {
        resultado.falhas.push({ pedido, falha });
      }
    }
    return resultado;
  }

  /** Abre uma caixa de decisão, limpando a falha que a anterior tenha deixado. */
  function abrirDecisao(proxima: DecisaoAberta) {
    setErroDaCaixa(null);
    setDecisao(proxima);
  }

  /**
   * Busca as fotos da folha de aprovação dos pedidos que saem da cotação e a
   * monta, o que abre a janela de impressão.
   *
   * Cumpre assim que a folha é montada, sem esperar a janela fechar, e é nesse
   * ponto que quem chamou deverá mandar a gravação: antes, ela apagaria e
   * reinseriria os itens enquanto as fotos ainda estão sendo pedidas, e a foto
   * pedida no meio da troca não acharia o item.
   *
   * @param lote - Os pedidos da folha, na ordem em que saem no papel.
   * @returns `fechada`, que cumpre quando a janela de impressão fecha, ou `null`
   *   quando a busca de alguma foto falhou e a folha não saiu.
   */
  async function abrirFolha(
    lote: readonly PedidoCompra[],
  ): Promise<{ fechada: Promise<void> } | null> {
    let prontos: PedidoNaFolha[];
    try {
      prontos = await prepararFolha(lote.map(pedidoGravadoParaFolha));
    } catch {
      return null;
    }
    return { fechada: folhaDeAprovacao.imprimir(prontos) };
  }

  /**
   * Avança o pedido, ou abre a caixa quando a etapa exige uma decisão.
   *
   * A saída da cotação imprime a folha de aprovação no mesmo clique, como a
   * origem: as fotos são buscadas, a folha é montada e só então a gravação
   * sai, sem esperar a janela de impressão fechar. A gravação que falha aparece
   * no bloco de erro, ainda que a folha já tenha saído.
   */
  async function handleAvancar(pedido: PedidoCompra) {
    setErroDaAcao(null);
    const destino = proximoStatus(pedido.status);

    if (destino.decisao !== null) {
      abrirDecisao({ modo: destino.decisao, pedidos: [pedido] });
      return;
    }

    const folha =
      pedido.status === 'em_cotacao' && destino.status === 'em_aprovacao'
        ? await abrirFolha([pedido])
        : undefined;

    try {
      await gravarTransicao(pedido, { status: destino.status });
    } catch (falha) {
      setErroDaAcao(mensagemDaFalha(falha, 'Não foi possível avançar.'));
      return;
    }

    if (folha === null) setErroDaAcao(avisoDaFolhaQueNaoSaiu(1));
  }

  /**
   * O que a volta de uma etapa apaga no pedido.
   *
   * @param pedido - O pedido que está retrocedendo.
   * @returns Os campos a gravar, já com a etapa de destino.
   */
  function mudancasDoRetrocesso(pedido: PedidoCompra): Partial<PedidoCompra> {
    const destino = statusAnterior(pedido.status, pedido.motivoReprovacao !== null);

    const mudancas: Partial<PedidoCompra> = { status: destino.status };
    if (destino.limpar.includes('aprovacao')) {
      mudancas.aprovador = null;
      mudancas.dataAprovacao = null;
    }
    if (destino.limpar.includes('compra')) mudancas.compradoPor = null;
    if (destino.limpar.includes('reprovacao')) mudancas.motivoReprovacao = null;
    return mudancas;
  }

  /**
   * Retrocede o pedido, apagando o que a etapa de onde ele sai preencheu.
   *
   * O pedido que já gerou despesa no caixa não volta direto: a pergunta sobre o
   * destino dela vem antes, porque apagar sozinho tiraria de lá um lançamento que
   * alguém pode já ter conciliado, e manter sozinho deixaria uma despesa de uma
   * compra que deixou de existir.
   */
  async function handleRetroceder(pedido: PedidoCompra) {
    setErroDaAcao(null);

    if (pedido.status === 'comprado' && pedido.lancadoNoFinanceiro) {
      setErroDaCaixa(null);
      setADesfazer({ pedidos: [pedido], lote: false });
      return;
    }

    try {
      await gravarTransicao(pedido, mudancasDoRetrocesso(pedido));
    } catch (falha) {
      setErroDaAcao(mensagemDaFalha(falha, 'Não foi possível voltar.'));
    }
  }

  /**
   * Conclui a caixa que gravou um ou mais pedidos com a mesma resposta.
   *
   * Tudo gravado fecha a caixa. Nada gravado deixa a caixa aberta com a falha
   * dentro dela — a mensagem da API para um pedido, a lista dos números para
   * vários. Parte gravada fecha a caixa, porque os gravados já não estão na
   * etapa em que a caixa os encontrou, e a falha dos outros vai para o bloco de
   * erro da página, com eles ainda marcados.
   *
   * @returns Verdadeiro quando a caixa fechou com tudo gravado.
   */
  function concluirCaixa(
    { gravados, falhas }: ResultadoDoLote,
    acao: string,
    padrao: string,
    fechar: () => void,
  ): boolean {
    const primeira = falhas[0];
    if (primeira === undefined) {
      fechar();
      return true;
    }
    if (gravados.length === 0) {
      setErroDaCaixa(
        falhas.length === 1
          ? mensagemDaFalha(primeira.falha, padrao)
          : mensagemDoLote(acao, falhas),
      );
      return false;
    }
    fechar();
    selecao.limpar();
    selecao.marcar(
      falhas.map(({ pedido }) => pedido.id),
      true,
    );
    setErroDaAcao(mensagemDoLote(acao, falhas));
    return false;
  }

  /**
   * Retrocede os pedidos que têm despesa, com o destino que a pessoa escolheu.
   *
   * Vindos do lote, a seleção se esvazia quando todos gravam; vindo do cartão,
   * a seleção fica como estava.
   *
   * @param lancamentos - `excluir` apaga as despesas geradas; `manter` as deixa.
   */
  async function handleConfirmarRetrocesso(lancamentos: 'excluir' | 'manter') {
    if (aDesfazer === null) return;
    setErroDaCaixa(null);

    setEmLote(true);
    const resultado = await gravarEmSequencia(
      aDesfazer.pedidos,
      mudancasDoRetrocesso,
      lancamentos,
    );
    setEmLote(false);
    const tudoGravado = concluirCaixa(resultado, 'voltar', 'Não foi possível voltar.', () =>
      setADesfazer(null),
    );
    if (tudoGravado && aDesfazer.lote) selecao.limpar();
  }

  /**
   * Grava a transição de uma caixa de decisão em cada pedido dela e a fecha, ou
   * mostra a falha.
   *
   * A decisão confirmada esvazia a seleção, venha a caixa de um cartão ou do
   * lote.
   *
   * @param mudancas - Os campos que a decisão preenche, com a etapa de destino.
   * @param acao - O verbo da mensagem de falha do lote, como "aprovar".
   * @param padrao - O texto para uma falha que nem chegou ao servidor.
   * @returns Os que gravaram e os que falharam, ou `null` sem caixa aberta.
   */
  async function gravarDecisao(
    mudancas: Partial<PedidoCompra>,
    acao: string,
    padrao: string,
  ): Promise<ResultadoDoLote | null> {
    if (decisao.modo === 'fechada') return null;
    setErroDaCaixa(null);

    setEmLote(true);
    const resultado = await gravarEmSequencia(decisao.pedidos, () => mudancas);
    setEmLote(false);
    const tudoGravado = concluirCaixa(resultado, acao, padrao, () =>
      setDecisao({ modo: 'fechada' }),
    );
    if (tudoGravado) selecao.limpar();
    return resultado;
  }

  /**
   * Grava quem comprou em cada pedido da caixa, ou recusa a compra de todos, e
   * mostra o que foi lançado no Financeiro.
   *
   * Basta um pedido com item sem categoria financeira para nenhum gravar: a
   * caixa continua aberta, com o número de cada pendente, e quem decidiu abre
   * cada um para classificar os itens. Gravar só os que estão em ordem deixaria
   * a seleção partida entre duas etapas por uma razão que a caixa já sabia.
   *
   * Depois da gravação abre "Lançado no Financeiro" com as despesas dos pedidos
   * que gravaram e ainda não tinham despesa — o que voltou a `comprado`
   * mantendo a de antes não lança nada novo, e fica fora da lista. Com parte do
   * lote recusada, a caixa lista só os que gravaram, e a falha dos outros vai
   * para o bloco de erro.
   *
   * @param compradoPor - Quem fez a compra, a resposta da caixa.
   */
  async function handleConfirmarCompra(compradoPor: string) {
    if (decisao.modo === 'fechada') return;
    const pendentes = decisao.pedidos.filter(
      (pedido) => posicoesSemCategoria(pedido.itens).length > 0,
    );
    if (pendentes.length > 0) {
      setErroDaCaixa(
        avisoDaCompraSemCategoriaNoQuadro(pendentes.map((pedido) => pedido.numero)),
      );
      return;
    }

    const antes = new Map(decisao.pedidos.map((pedido) => [pedido.id, pedido]));
    const resultado = await gravarDecisao(
      { status: 'comprado', compradoPor },
      'registrar a compra',
      'Não foi possível registrar.',
    );
    if (resultado === null) return;

    setDespesasLancadas(
      resultado.gravados.flatMap((gravado) => {
        const anterior = antes.get(gravado.id);
        return anterior !== undefined && lancaNaVirada(anterior, gravado.status)
          ? despesasAvisadas(gravado)
          : [];
      }),
    );
  }

  /**
   * Marca ou desmarca cartões do quadro.
   *
   * @param marcados - Um pedido, pela caixa do cartão, ou os da coluna, pelo
   *   "Todos".
   * @param marcado - O estado novo das caixas.
   */
  function handleSelecionar(marcados: readonly PedidoCompra[], marcado: boolean) {
    selecao.marcar(
      marcados.map((pedido) => pedido.id),
      marcado,
    );
  }

  /** Imprime a folha de aprovação dos marcados, sem mexer na seleção. */
  async function handleExportarPdf() {
    setErroDaAcao(null);
    if (selecionados.length === 0) return;
    try {
      await folhaDeAprovacao.gerar(selecionados.map(pedidoGravadoParaFolha));
    } catch {
      setErroDaAcao(
        'A folha de aprovação não saiu: não foi possível carregar as fotos dos itens.',
      );
    }
  }

  /**
   * Avança cada pedido marcado pela regra do botão do cartão.
   *
   * Os que saem de "Em Cotação" vão juntos para uma folha só, que imprime no
   * mesmo clique, enquanto as gravações andam. Os que não pedem decisão gravam
   * em sequência e saem da seleção. Os que pedem quem aprovou continuam
   * marcados e abrem uma caixa só; os que pedem quem comprou também, mas só
   * quando não há nenhum à espera da aprovação — esses abrem a caixa deles no
   * clique seguinte. A caixa só abre depois que a janela de impressão fecha.
   */
  async function handleAvancarSelecionados() {
    setErroDaAcao(null);
    const lote = selecionados;
    if (lote.length === 0) return;

    const paraAprovacao: PedidoCompra[] = [];
    const paraCompra: PedidoCompra[] = [];
    const diretos: PedidoCompra[] = [];
    for (const pedido of lote) {
      const { decisao: pedeDecisao } = proximoStatus(pedido.status);
      if (pedeDecisao === 'aprovacao') paraAprovacao.push(pedido);
      else if (pedeDecisao === 'compra') paraCompra.push(pedido);
      else diretos.push(pedido);
    }

    setEmLote(true);
    const daCotacao = diretos.filter((pedido) => pedido.status === 'em_cotacao');
    const folha = daCotacao.length > 0 ? await abrirFolha(daCotacao) : undefined;

    const { gravados, falhas } = await gravarEmSequencia(diretos, (pedido) => ({
      status: proximoStatus(pedido.status).status,
    }));
    selecao.marcar(
      gravados.map((pedido) => pedido.id),
      false,
    );
    setEmLote(false);

    const mensagens: string[] = [];
    if (falhas.length > 0) mensagens.push(mensagemDoLote('avançar', falhas));
    setErroDaAcao(mensagens.length > 0 ? mensagens.join(' ') : null);

    if (folha === null) {
      mensagens.push(avisoDaFolhaQueNaoSaiu(daCotacao.length));
      setErroDaAcao(mensagens.join(' '));
    } else if (folha !== undefined) {
      await folha.fechada;
    }

    if (paraAprovacao.length > 0) abrirDecisao({ modo: 'aprovacao', pedidos: paraAprovacao });
    else if (paraCompra.length > 0) abrirDecisao({ modo: 'compra', pedidos: paraCompra });
  }

  /**
   * Retrocede cada pedido marcado pela regra do botão do cartão.
   *
   * O que está na primeira coluna não tem para onde voltar e só sai da
   * seleção. Os que já geraram despesa continuam marcados e entram juntos numa
   * pergunta só sobre os lançamentos, que vale para todos eles; os demais
   * gravam em sequência e saem da seleção.
   */
  async function handleRetrocederSelecionados() {
    setErroDaAcao(null);
    const lote = selecionados;
    if (lote.length === 0) return;

    const comDespesa: PedidoCompra[] = [];
    const semVolta: PedidoCompra[] = [];
    const diretos: PedidoCompra[] = [];
    for (const pedido of lote) {
      if (pedido.status === 'comprado' && pedido.lancadoNoFinanceiro) comDespesa.push(pedido);
      else if (statusAnterior(pedido.status, false).status === pedido.status) {
        semVolta.push(pedido);
      } else diretos.push(pedido);
    }

    setEmLote(true);
    const { gravados, falhas } = await gravarEmSequencia(diretos, mudancasDoRetrocesso);
    selecao.marcar(
      [...semVolta, ...gravados].map((pedido) => pedido.id),
      false,
    );
    setErroDaAcao(falhas.length > 0 ? mensagemDoLote('voltar', falhas) : null);

    if (comDespesa.length > 0) {
      setErroDaCaixa(null);
      setADesfazer({ pedidos: comDespesa, lote: true });
    }
    setEmLote(false);
  }

  /**
   * Fecha "Lançado no Financeiro" e tira as despesas do estado da navegação,
   * para que recarregar a página não abra a caixa de novo.
   */
  function handleFecharLancados() {
    setDespesasLancadas([]);
    if (location.state !== null)
      void navigate(location.pathname, { replace: true, state: null });
  }

  /** Trava as caixas durante a gravação, inclusive entre um pedido e outro do lote. */
  const ocupada = salvar.isPending || emLote;
  const quantidadeNaCaixa = decisao.modo === 'fechada' ? 1 : decisao.pedidos.length;

  return (
    <>
      <div className={cn(folhaDeAprovacao.folha !== null && 'print:hidden')}>
        <CabecalhoDoModulo
          titulo={ROTULOS_MODULO.compras}
          acoes={
            podeEditar ? (
              <Button onClick={() => navigate('/compras/novo')}>
                + Novo pedido de compra
              </Button>
            ) : undefined
          }
        />

        {erroDaAcao !== null && (
          <p className="mb-4 rounded-sm border border-perigo/40 bg-perigo/5 p-3 text-sm text-perigo">
            {erroDaAcao}
          </p>
        )}

        {consultaPedidos.isPending ? (
          <Skeleton className="h-64 w-full" />
        ) : consultaPedidos.error !== null ? (
          <div className="rounded-sm border border-perigo/40 bg-perigo/5 p-6">
            <p className="mb-3 text-sm text-perigo">Não foi possível carregar os pedidos.</p>
            <Button size="sm" onClick={() => void consultaPedidos.refetch()}>
              Tentar de novo
            </Button>
          </div>
        ) : (
          <>
            {selecionados.length > 0 && (
              <div className="sticky top-0 z-[5] mx-5 mb-1 flex flex-wrap items-center gap-3 rounded-md border border-linha-forte bg-superficie-2 px-5 py-2.5 print:hidden">
                <span className="mr-auto font-mono text-xs leading-[1.6] text-texto-medio">
                  {selecionados.length === 1
                    ? '1 pedido selecionado'
                    : `${String(selecionados.length)} pedidos selecionados`}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  disabled={emLote}
                  onClick={() => void handleExportarPdf()}
                >
                  🖨 Exportar PDF
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={emLote}
                  onClick={() => void handleRetrocederSelecionados()}
                >
                  ← Retroceder selecionados
                </Button>
                <Button
                  type="button"
                  disabled={emLote}
                  onClick={() => void handleAvancarSelecionados()}
                >
                  Avançar selecionados →
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={emLote}
                  onClick={selecao.limpar}
                >
                  Limpar seleção
                </Button>
              </div>
            )}

            {pedidos.length === 0 ? (
              <Vazio mensagem="Nenhum pedido de compra registrado ainda." />
            ) : (
              <QuadroCompras
                pedidos={pedidos}
                onAbrir={
                  podeEditar ? (pedido) => navigate(`/compras/${pedido.id}`) : undefined
                }
                onAvancar={podeEditar ? (pedido) => void handleAvancar(pedido) : undefined}
                onRetroceder={
                  podeEditar ? (pedido) => void handleRetroceder(pedido) : undefined
                }
                onReprovar={
                  podeEditar
                    ? (pedido) => abrirDecisao({ modo: 'reprovacao', pedidos: [pedido] })
                    : undefined
                }
                selecionados={selecao.ids}
                onSelecionar={podeEditar ? handleSelecionar : undefined}
              />
            )}

            <TabelaFinalizados
              pedidos={pedidos}
              onAbrir={podeEditar ? (pedido) => navigate(`/compras/${pedido.id}`) : undefined}
              onReabrir={podeEditar ? (pedido) => void handleRetroceder(pedido) : undefined}
            />
          </>
        )}

        <CaixaDeAprovacao
          aberta={decisao.modo === 'aprovacao'}
          ocupada={ocupada}
          erro={erroDaCaixa}
          quantidade={quantidadeNaCaixa}
          onConfirmar={(aprovador) =>
            void gravarDecisao(
              {
                status: 'em_processo_de_compra',
                aprovador,
                dataAprovacao: hojeEmIso(),
              },
              'aprovar',
              'Não foi possível aprovar.',
            )
          }
          onReprovar={() => {
            if (decisao.modo === 'aprovacao') {
              abrirDecisao({ modo: 'reprovacao', pedidos: decisao.pedidos });
            }
          }}
          onCancelar={() => setDecisao({ modo: 'fechada' })}
        />

        <CaixaDeReprovacao
          aberta={decisao.modo === 'reprovacao'}
          ocupada={ocupada}
          erro={erroDaCaixa}
          quantidade={quantidadeNaCaixa}
          onConfirmar={(motivo) =>
            void gravarDecisao(
              { status: 'finalizado', motivoReprovacao: motivo },
              'reprovar',
              'Não foi possível reprovar.',
            )
          }
          onCancelar={() => setDecisao({ modo: 'fechada' })}
        />

        <CaixaDeCompra
          aberta={decisao.modo === 'compra'}
          ocupada={ocupada}
          erro={erroDaCaixa}
          quantidade={quantidadeNaCaixa}
          onConfirmar={(compradoPor) => void handleConfirmarCompra(compradoPor)}
          onCancelar={() => setDecisao({ modo: 'fechada' })}
        />

        <AvisoLancadoNoFinanceiro
          despesas={despesasLancadas}
          onFechar={handleFecharLancados}
        />

        <AlertDialog
          open={aDesfazer !== null}
          onOpenChange={(aberto) => !aberto && setADesfazer(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader className="mb-0">
              <AlertDialogTitle>Excluir lançamento no Financeiro?</AlertDialogTitle>
              <AlertDialogDescription>
                {aDesfazer !== null && aDesfazer.pedidos.length > 1
                  ? `${String(aDesfazer.pedidos.length)} dos pedidos selecionados já geraram lançamento de despesa no Financeiro. Ao retroceder, deseja excluir esses lançamentos?`
                  : 'Este pedido já gerou lançamento de despesa no Financeiro. Ao retroceder, deseja excluir esse lançamento?'}
              </AlertDialogDescription>
            </AlertDialogHeader>

            {erroDaCaixa !== null && (
              <p role="alert" className="mt-3 text-sm text-perigo">
                {erroDaCaixa}
              </p>
            )}

            <div className="mt-1.5">
              <Button
                variant="destructive"
                className="w-full"
                onClick={() => void handleConfirmarRetrocesso('excluir')}
                disabled={ocupada}
              >
                ✓ Sim, excluir lançamento
              </Button>
            </div>
            <AlertDialogFooter>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => void handleConfirmarRetrocesso('manter')}
                disabled={ocupada}
              >
                Não, manter lançamento
              </Button>
            </AlertDialogFooter>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={ocupada}>
                Cancelar (não retroceder)
              </AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {folhaDeAprovacao.folha !== null && (
        <FolhaDeAprovacao
          key={folhaDeAprovacao.folha.geradaEm}
          pedidos={folhaDeAprovacao.folha.pedidos}
          geradaEm={folhaDeAprovacao.folha.geradaEm}
          onConcluida={folhaDeAprovacao.concluir}
        />
      )}
    </>
  );
}
